import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import FeedbackModal from '../components/FeedbackModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { apiService } from '../services/api';
import './Feedback.css';

const Feedback = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);

  const parseRatingValue = (value) => {
    const ratingMap = {
      '0_Excellent': 5,
      '1_Good': 4,
      '2_Average': 3,
      '3_Poor': 2
    };

    if (value === undefined || value === null) {
      return 0;
    }

    if (typeof value === 'number') {
      return Math.max(0, Math.min(5, value));
    }

    const trimmed = String(value).trim();
    if (ratingMap[trimmed] !== undefined) {
      return ratingMap[trimmed];
    }

    const numeric = Number(trimmed);
    return Number.isNaN(numeric) ? 0 : Math.max(0, Math.min(5, numeric));
  };

  const normalizeFeedback = (item) => ({
    id: item._id || item.id,
    userName: item.customerName || item.userName || 'Anonymous',
    userEmail: item.customerEmail || item.email || '',
    userPhone: item.customerPhone || item.phone || '',
    rating: parseRatingValue(item.rating),
    category: item.category || 'general',
    comment: item.comment || item.message || '',
    createdAt: item.createdAt || item.created_at || new Date().toISOString(),
    status: item.status || 'received',
    avatar: item.avatar || '👤'
  });

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await apiService.feedback.getAll({ limit: 200 });
      setFeedbacks((response.data.data || []).map(normalizeFeedback));
    } catch (error) {
      console.error('Fetch feedback error:', error);
      toast.error('Failed to load feedback.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesSearch = 
      feedback.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feedback.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feedback.comment.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = 
      selectedFilter === 'all' ||
      (selectedFilter === 'received' && feedback.status === 'received') ||
      (selectedFilter === 'processed' && feedback.status === 'processed') ||
      (selectedFilter === 'ignored' && feedback.status === 'ignored') ||
      (selectedFilter === 'excellent' && feedback.rating === 5) ||
      (selectedFilter === 'poor' && feedback.rating <= 2);

    return matchesSearch && matchesFilter;
  });

  const handleAddFeedback = () => {
    setIsModalOpen(true);
  };

  const handleSaveFeedback = async (formData) => {
    try {
      const response = await apiService.feedback.create({
        customerName: formData.userName,
        customerEmail: formData.userEmail,
        customerPhone: formData.userPhone,
        rating: Number(formData.rating),
        category: formData.category,
        comment: formData.comment,
        avatar: formData.avatar,
        status: 'received'
      });

      const newFeedback = normalizeFeedback(response.data.data);
      setFeedbacks(prev => [newFeedback, ...prev]);
      setIsModalOpen(false);
      toast.success('Feedback submitted successfully!');
    } catch (error) {
      console.error('Create feedback error:', error);
      toast.error('Failed to save feedback.');
    }
  };

  const handleDeleteFeedback = (feedback) => {
    setFeedbackToDelete(feedback);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!feedbackToDelete) {
      return;
    }

    try {
      await apiService.feedback.delete(feedbackToDelete.id);
      setFeedbacks(prev => prev.filter(f => f.id !== feedbackToDelete.id));
      setIsDeleteModalOpen(false);
      setFeedbackToDelete(null);
      toast.success('Feedback deleted successfully.');
    } catch (error) {
      console.error('Delete feedback error:', error);
      toast.error('Failed to delete feedback.');
    }
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      const response = await apiService.feedback.update(feedbackId, { status: newStatus });
      const updatedFeedback = normalizeFeedback(response.data.data);
      setFeedbacks(prev => prev.map(f => f.id === feedbackId ? updatedFeedback : f));
    } catch (error) {
      console.error('Update feedback status error:', error);
      toast.error('Failed to update feedback status.');
    }
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'service': '#3b82f6',
      'food': '#ef4444',
      'cleanliness': '#10b981',
      'value': '#f59e0b',
      'ambiance': '#8b5cf6'
    };
    return colors[category] || '#6b7280';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      received: { label: 'New', icon: '🆕' },
      processed: { label: 'Reviewed', icon: '✓' },
      ignored: { label: 'Ignored', icon: '🚫' }
    };

    const { label, icon } = statusMap[status] || { label: status, icon: '' };
    return (
      <span className={`status-badge ${status}`}>
        {icon} {label.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="feedback-container">
      <div className="feedback-header">
        <div className="header-content">
          <h1>Customer Feedback</h1>
          <p>Manage and respond to customer reviews and ratings</p>
        </div>
        <button className="btn-add-feedback" onClick={handleAddFeedback}>
          <span>+</span> Add Feedback
        </button>
      </div>

      <div className="feedback-toolbar">
        <div className="search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, email, or comment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('all')}
          >
            All ({feedbacks.length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'received' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('received')}
          >
            New ({feedbacks.filter(f => f.status === 'received').length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'processed' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('processed')}
          >
            Reviewed ({feedbacks.filter(f => f.status === 'processed').length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'ignored' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('ignored')}
          >
            Ignored ({feedbacks.filter(f => f.status === 'ignored').length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'excellent' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('excellent')}
          >
            Excellent ⭐ ({feedbacks.filter(f => f.rating === 5).length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'poor' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('poor')}
          >
            Poor ⚠️ ({feedbacks.filter(f => f.rating <= 2).length})
          </button>
        </div>
      </div>

      <div className="feedback-list">
        {filteredFeedbacks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3>No feedback found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          filteredFeedbacks.map(feedback => (
            <div key={feedback.id} className="feedback-card">
              <div className="feedback-header-row">
                <div className="feedback-user-info">
                  <span className="user-avatar">{feedback.avatar}</span>
                  <div className="user-details">
                    <h3 className="user-name">{feedback.userName}</h3>
                    <p className="user-contact">{feedback.userEmail} • {feedback.userPhone}</p>
                  </div>
                </div>
                <div className="feedback-meta">
                  {getStatusBadge(feedback.status)}
                  <span className="feedback-date">{formatDate(feedback.createdAt)}</span>
                </div>
              </div>

              <div className="feedback-rating">
                <div className="rating-display">
                  <span className="stars">{getRatingStars(feedback.rating)}</span>
                  <span className="rating-number">{feedback.rating}/5</span>
                </div>
                <span 
                  className="category-tag" 
                  style={{ backgroundColor: getCategoryColor(feedback.category) }}
                >
                  {feedback.category.charAt(0).toUpperCase() + feedback.category.slice(1)}
                </span>
              </div>

              <div className="feedback-comment">
                <p>{feedback.comment}</p>
              </div>

              <div className="feedback-actions">
                <div className="status-selector">
                  <select 
                    value={feedback.status}
                    onChange={(e) => handleStatusChange(feedback.id, e.target.value)}
                    className="status-dropdown"
                  >
                    <option value="received">New</option>
                    <option value="processed">Reviewed</option>
                    <option value="ignored">Ignored</option>
                  </select>
                </div>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteFeedback(feedback)}
                  title="Delete feedback"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFeedback}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFeedbackToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={feedbackToDelete?.userName || ''}
      />
    </div>
  );
};

export default Feedback;
